<template>
  <div
    v-if="isMetaReady && Object.keys(sampleFieldsModels).length"
    class="samples-form"
  >
    <header
      v-if="sampleTypes.length > 0 || !showCsv"
      class="samples-form-header"
    >
      <div v-if="sampleTypes.length > 0" class="sample-type-control">
        <label class="label">Sample Type</label>
        <div
          v-tooltip="sampleTypes.length <= 1 && 'Cannot change sample type'"
          class="control select is-small"
          :class="{ 'is-danger': !sampleTypeId }"
        >
          <select
            :key="`select-${sampleTypeId}`"
            :disabled="sampleTypes.length <= 1"
            @change="(e) => (sampleTypeId = e.target.value)"
          >
            <option
              v-for="sampleTypeOpt in sampleTypes"
              :key="`opt-${sampleTypeOpt.id}`"
              :value="sampleTypeOpt.id"
              :selected="
                sampleTypeId.toUpperCase() === sampleTypeOpt.id.toUpperCase()
              "
            >
              {{ sampleTypeOpt.label }}
            </option>
          </select>
        </div>
      </div>
      <BaseButton
        v-if="!showCsv"
        style="margin-left: auto"
        size="small"
        :outlined="true"
        icon="fa fa-file-csv"
        label="Initialise from CSV"
        @click="showCsv = true"
      />
    </header>
    <div v-if="showCsv" class="is-flex is-flex-direction-column">
      <header class="label mb-2">
        <span class="icon has-text-info">
          <i class="fa-solid fa-info-circle" />
        </span>
        You can initialise the sample creation form by pasting some sample
        information or
        <a @click="rawSamples = [{}]">continue directly</a>
        to an empty form
      </header>
      <CreateSamplesCsv
        class="mb-2"
        :sample-type-id="sampleTypeId"
        @new-samples="csvSamples = $event"
      />
      <div class="is-flex is-justify-content-flex-end mx-2 mt-2">
        <button
          v-if="!(csvSamples && csvSamples.length)"
          class="button is-primary mr-2"
          :class="{ 'is-outlined': csvSamples && csvSamples.length }"
          @click="rawSamples = [{}]"
        >
          Continue (empty form)
        </button>
        <button
          v-if="csvSamples && csvSamples.length"
          class="button is-primary"
          :class="{ 'is-outlined': !(csvSamples && csvSamples.length) }"
          @click="rawSamples = csvSamples"
        >
          Continue with {{ csvSamples.length }} samples
        </button>
      </div>
    </div>
    <div v-else class="samples-form-body" style="height: 100%; flex-grow: 1">
      <SelectableRows
        :rows="formSamples"
        @selected-indices="(indices) => (selectedSampleIndices = indices)"
      >
        <template
          #default="{
            selectableRows,
            toggleSelectRow,
            toggleSelectAllRows,
            allRowsSelected,
          }"
        >
          <table
            class="sample-form-table table is-striped"
            style="min-width: 100%"
          >
            <thead>
              <tr>
                <th>
                  <div class="checkbox-header">
                    <input
                      type="checkbox"
                      :checked="allRowsSelected"
                      @click.stop="toggleSelectAllRows"
                    />
                    <IconButton
                      icon="fa fa-trash"
                      color="danger"
                      size="small"
                      tooltip="Delete selected rows"
                      :badge="
                        selectedSampleIndices.length > 0
                          ? selectedSampleIndices.length
                          : null
                      "
                      :disabled="selectedSampleIndices.length < 1"
                      @click="deleteSelectedRows"
                    />
                  </div>
                </th>
                <th class="second-cell">Name</th>
                <th
                  v-for="(fieldName, fieldIndex) in sampleFieldsIter"
                  :key="`${fieldIndex}-field${fieldName}-head`"
                >
                  <div class="field-header">
                    <span v-if="sampleFieldsModels[fieldName]?.label">
                      {{ sampleFieldsModels[fieldName].label }}
                    </span>
                    <div
                      v-if="headerFields[fieldName]"
                      class="field-header-controls"
                    >
                      <FieldComponent
                        :key="`header-${fieldName}`"
                        style="flex: 1"
                        :field-meta="sampleFieldsModels[fieldName]"
                        :is-edit-mode="true"
                        :data="headerFields[fieldName]"
                        :bypass-validation="['required']"
                        :field-id="fieldName"
                        validation-scope="header"
                        :max-height="200"
                        @edit="(value) => (headerFields[fieldName] = { value })"
                      />
                      <IconButton
                        icon="fa fa-list-check"
                        color="primary"
                        size="small"
                        tooltip="Propagate value to selected rows"
                        :badge="
                          selectedSampleIndices.length > 0 &&
                          !isEmpty(headerFields[fieldName].value) &&
                          !(vHead$ && vHead$[fieldName]?.$errors?.length > 0)
                            ? selectedSampleIndices.length
                            : null
                        "
                        :disabled="
                          selectedSampleIndices.length < 1 ||
                          isEmpty(headerFields[fieldName].value) ||
                          (vHead$ && vHead$[fieldName]?.$errors?.length > 0)
                        "
                        @click="propagateValue(fieldName, true)"
                      />
                      <IconButton
                        icon="fa fa-list-ul"
                        color="primary"
                        size="small"
                        tooltip="Propagate value to all rows"
                        :disabled="
                          isEmpty(headerFields[fieldName].value) ||
                          vHead$[fieldName]?.$errors?.length > 0
                        "
                        @click="propagateValue(fieldName, false)"
                      />
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="({ item, selected }, index) in selectableRows"
                :key="`vis-${item._id}`"
              >
                <template v-if="item">
                  <td>
                    <input
                      type="checkbox"
                      :checked="selected"
                      @click="
                        (event) => {
                          clickEvent = event
                          toggleSelectRow(index, event.shiftKey)
                        }
                      "
                    />
                  </td>
                  <td>
                    <FieldComponent
                      :key="`fc-name-${index}-${item._id}`"
                      :field-meta="sampleFieldsModels['name']"
                      :is-edit-mode="true"
                      :data="formSamples[index].name"
                      :max-height="200"
                      :field-id="`name${index}`"
                      validation-scope="createNewSamples"
                      @edit="(value) => (formSamples[index].name = { value })"
                    />
                  </td>
                  <td
                    v-for="(fieldName, fieldIndex) in sampleFieldsIter"
                    :key="`${fieldIndex}-field${fieldName}-value`"
                  >
                    <FieldComponent
                      :key="`fc-${fieldName}-${index}-${item._id}`"
                      :field-meta="sampleFieldsModels[fieldName]"
                      :is-edit-mode="true"
                      :data="formSamples[index][fieldName]"
                      :max-height="200"
                      :field-id="`${fieldName}${index}`"
                      validation-scope="createNewSamples"
                      @edit="
                        (value) => {
                          formSamples[index][fieldName] = { value }
                        }
                      "
                    />
                  </td>
                </template>
              </tr>
              <tr>
                <td>
                  <BaseButton
                    size="small"
                    variant="primary"
                    :icon="['fa', 'fa-plus']"
                    @click="addSamples(addSampleNumber)"
                  />
                </td>
                <td>
                  <div class="control">
                    <input
                      v-model="addSampleNumber"
                      class="input is-small"
                      type="number"
                      @keyup.enter="addSamples(addSampleNumber)"
                    />
                  </div>
                </td>
                <td colspan="99"></td>
              </tr>
            </tbody>
          </table>
        </template>
      </SelectableRows>
    </div>
    <div class="samples-form-footer"></div>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { cloneDeep, isEmpty } from 'lodash-es'
import { v4 as uuid } from 'uuid'
import { computed, ref, watch } from 'vue'

import { useSample } from '@/composables/useSample'

import CreateSamplesCsv from '@/components/CreateSamplesCsv.vue'
import SelectableRows from '@/components/SelectableRows.vue'

export default {
  name: 'CreateSamples',
  components: {
    SelectableRows,
    CreateSamplesCsv,
  },
  props: {
    initTypeId: {
      type: String,
      default: 'genericsample',
    },
    initSamples: {
      type: Array,
      default: () => [],
    },
    sampleTypes: {
      type: Array,
      default: () => [],
    },
    requiredFields: {
      type: Array,
      default: () => ['specimen', 'primary_project', 'organism'],
    },
  },
  setup(props, { emit }) {
    const formSamples = ref([])
    const sampleTypeId = ref('')
    watch(
      () => props.initTypeId,
      (propId) => (sampleTypeId.value = propId),
      { immediate: true },
    )
    watch(
      () => props.sampleTypes,
      (sampleTypeOptions) => {
        if (
          !sampleTypeId.value &&
          sampleTypeOptions.length >= 1 &&
          sampleTypeOptions[0]?.id
        ) {
          sampleTypeId.value = sampleTypeOptions[0]?.id
        }
      },
    )

    /* Validation */
    let v$ = useVuelidate({}, {}, { $scope: 'createNewSamples' })
    let vHead$ = useVuelidate({}, {}, { $scope: 'header' })
    const validationErrors = computed(() => v$?.value?.$errors || [])

    const {
      sampleMeta,
      isMetaReady,
      sampleFieldsNames,
      sampleFieldsModels,
      defaultModifiableSample: defaultSample,
    } = useSample(sampleTypeId, props.requiredFields)

    const sampleFieldsIter = computed(() =>
      sampleFieldsNames.value.filter((f) => f !== 'name'),
    )

    const headerFields = ref({})
    function getNewDefaultSample() {
      // Attach an ID to a default sample object copy
      // const sample =
      return { ...cloneDeep(defaultSample.value), _id: uuid() }
    }
    function updateFields(currentFields) {
      // Transfer current field values to a new sample
      const newFields = getNewDefaultSample()
      return Object.entries(newFields).reduce(
        (fields, [fieldName, defaultValue]) => ({
          ...fields,
          [fieldName]: currentFields[fieldName] || defaultValue,
        }),
        {},
      )
    }
    watch(
      defaultSample,
      () => {
        // update fields in default samples (when changing sample type)
        headerFields.value = updateFields(headerFields.value)
        formSamples.value = formSamples.value.map((s) => {
          // delete sample type to be later replaced with default value
          delete s.type
          return updateFields(s)
        })
      },
      { immediate: true, deep: true },
    )

    const showCsv = ref(true)
    const rawSamples = ref(props.initSamples)

    function initialiseSamples(samples = []) {
      return samples?.length >= 1 ? samples.map((s) => updateFields(s)) : []
    }
    watch(
      // Initialise form samples when base samples change (because of prop
      // change or when they are set by the CSV form)
      rawSamples,
      (s) => {
        formSamples.value = initialiseSamples(s)
        // show CSV form if no sample
        showCsv.value = formSamples.value.length === 0
      },
      { immediate: true },
    )

    const samplesById = computed(() => {
      return (
        validationErrors.value.length === 0 &&
        formSamples.value.reduce(
          (samplesById, formSample) => ({
            ...samplesById,
            [formSample._id]: {
              ...formSample,
            },
          }),
          {},
        )
      )
    })
    watch(samplesById, (s) => emit('new-samples', s))

    return {
      sampleMeta,
      isMetaReady,
      sampleFieldsNames,
      sampleFieldsIter,
      sampleFieldsModels,
      defaultSample,
      getNewDefaultSample,
      headerFields,
      vHead$,
      v$,
      validationErrors,
      rawSamples,
      samplesById,
      formSamples,
      sampleTypeId,
      showCsv,
      isEmpty,
    }
  },
  data() {
    return {
      selectedSampleIndices: [],
      addSampleNumber: 1,
      csvSamples: null,
    }
  },
  computed: {
    selectedSamples() {
      return this.selectedSampleIndices.map((i) => this.formSamples[i])
    },
  },
  methods: {
    getNumberNewDefaultSample(number) {
      return Array.from({ length: number }, () => this.getNewDefaultSample())
    },
    addSamples(number) {
      this.formSamples = [
        ...this.formSamples,
        ...this.getNumberNewDefaultSample(number),
      ]
      this.addSampleNumber = 1
    },
    deleteSelectedRows() {
      const selectedIndices = this.selectedSampleIndices
      this.formSamples = this.formSamples.filter(
        (_, i) => !selectedIndices.includes(i),
      )
      if (this.formSamples.length === 0) this.addSamples(1)
    },
    propagateValue(fieldName, toSelected = false) {
      const fieldValue = this.headerFields[fieldName]
      const samples = toSelected ? this.selectedSamples : this.formSamples
      for (let s of samples) s[fieldName] = fieldValue
    },
  },
}
</script>

<style scoped lang="scss">
@use '@/assets/styles/mixins/data-table' as *;

// ── Layout ──
.samples-form {
  display: flex;
  flex-direction: column;
}

.samples-form-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-3);
  padding: 0 var(--space-2);
  margin-bottom: var(--space-3);
}

.sample-type-control {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-shrink: 0;
}

.samples-form-body {
  overflow-x: auto;
}

// ── Table base (reuse data-table mixin) ──
table.sample-form-table {
  @include data-table-base;
  @include data-table-rows;

  th,
  td {
    vertical-align: top;
  }
}

// Non-frozen columns min width
table.sample-form-table th:nth-of-type(n + 3),
table.sample-form-table td:nth-of-type(n + 3) {
  min-width: 300px;
}

// ── Disable hover on non-data rows ──
table.sample-form-table thead tr,
table.sample-form-table tbody tr:last-child {
  cursor: default;
  &:hover {
    background-color: inherit;
  }
}

// ── Sticky header ──
table.sample-form-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: var(--bg-default);
  vertical-align: top;
}

// ── Frozen columns (checkbox + name) ──
$frozen-col1-width: 48px;

table.sample-form-table th:nth-of-type(1),
table.sample-form-table td:nth-of-type(1) {
  position: sticky;
  left: 0;
  z-index: 1;
  text-align: center;
  width: $frozen-col1-width;
  min-width: $frozen-col1-width;
  max-width: $frozen-col1-width;
}

table.sample-form-table th:nth-of-type(2),
table.sample-form-table td:nth-of-type(2) {
  position: sticky;
  left: $frozen-col1-width;
  z-index: 1;
  min-width: 300px;
}

// Frozen header corners: highest z-index
table.sample-form-table thead th:nth-of-type(-n + 2) {
  z-index: 3;
}

// Opaque backgrounds for frozen body cells
table.sample-form-table tbody td:nth-of-type(-n + 2) {
  background-color: var(--bg-base);
}

table.sample-form-table
  tbody
  tr:nth-child(even):not(.is-selected)
  td:nth-of-type(-n + 2) {
  background-color: var(--bg-stripe);
}

table.sample-form-table tbody tr.is-selected td:nth-of-type(-n + 2) {
  background-color: var(--bg-selected);
}

// Right edge on last frozen column (name)
table.sample-form-table td:nth-of-type(2),
table.sample-form-table th:nth-of-type(2) {
  box-shadow: 2px 0 0 var(--border-default);
}

// Footer: no shadows
table.sample-form-table tbody tr:last-child td {
  box-shadow: none;
}

// ── Add-row footer — no hover, distinct background, no borders ──
table.sample-form-table tbody tr:last-child {
  background-color: var(--bg-default);

  td {
    padding: var(--space-3);
    border-bottom: none;
    box-shadow: none;
    background-color: var(--bg-default);
  }
}

// ── Checkbox alignment ──
table.sample-form-table input[type='checkbox'] {
  margin: 0;
}

// ── Header layout ──
.checkbox-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-3);
  height: 100%;
}

.field-header {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.field-header-controls {
  display: flex;
  align-items: flex-start;
  gap: var(--space-1);
}
</style>
